import { Fragment, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, Collapse, Link, Paper, Stack, Table, TableBody,
  TableCell, TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import type { InboundRejected } from '@eprintsmart/shared';
import { admitParkedReply, dismissParkedReply, watchParkedReplies } from '../services/notifications';

/**
 * Email replies that could not be placed on an order.
 *
 * The webhook parks rather than drops anything it cannot verify, and the
 * commonest reason is entirely innocent: a customer replying from their work
 * address instead of the one they ordered with. Without this list that parking
 * bay is a bin with a nicer name, and the customer is still being ignored —
 * which is the exact failure inbound email was built to end.
 *
 * SO THE DEFAULT STATE OF THIS SECTION IS INVISIBLE. It renders nothing when
 * the bay is empty, because a permanently-present empty table teaches people to
 * stop seeing it, and the one time it has a row in it is the time it must be
 * noticed.
 */

const RELATIVE = (ms: number) => {
  const mins = Math.floor((Date.now() - ms) / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  if (mins < 1440) return `${Math.floor(mins / 60)}h ago`;
  return `${Math.floor(mins / 1440)}d ago`;
};

/**
 * Why it parked, said in a way that implies what to do about it.
 *
 * 'sender-mismatch' is the one staff will actually meet, and the wording has to
 * carry that it is usually fine — reading it as "suspicious" would get real
 * customers dismissed.
 */
const REASON: Record<InboundRejected['reason'], { label: string; hint: string }> = {
  'sender-mismatch': {
    label: 'Different address',
    hint: 'Sent from an address that is not the one on the order — usually the same person writing from work. Check the two addresses below before admitting.',
  },
  'unknown-recipient': {
    label: 'Unverified address',
    hint: 'The reply address did not verify, so we cannot tell which order this belongs to. Nothing to admit it to.',
  },
  'no-order': {
    label: 'Order gone',
    hint: 'The address verified but that order no longer exists.',
  },
  'empty-body': {
    label: 'Empty',
    hint: 'Nothing but quoted history or an attachment — attachments are not accepted yet.',
  },
};

export function ParkedReplies() {
  const [rows, setRows] = useState<InboundRejected[]>([]);
  const [open, setOpen] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => watchParkedReplies(setRows, (err) => setError(err.message)), []);

  /* Both actions are server calls, and the row vanishes from the snapshot when
     one lands — so there is nothing to remove here on success. What there is to
     do is keep the failure visible: a second admin who already decided this row
     gets a failed-precondition, and silently doing nothing would read as a
     broken button. */
  const decide = async (id: string, action: 'admit' | 'dismiss') => {
    setBusy(id);
    setError(null);
    try {
      if (action === 'admit') await admitParkedReply(id);
      else await dismissParkedReply(id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not do that.');
    } finally {
      setBusy(null);
    }
  };

  if (rows.length === 0) {
    // Still show an error if the listener itself failed — a missing index or a
    // rules change must not look like an empty bay.
    return error ? <Alert severity="error" onClose={() => setError(null)}>{error}</Alert> : null;
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>Replies we could not place</Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Typography variant="body2" color="text.secondary" gutterBottom>
        {rows.length} email {rows.length === 1 ? 'reply' : 'replies'} arrived that could not be
        matched to the customer on the order. Admitting one puts it on that order&apos;s thread as
        the customer&apos;s words, where they can see it. Dismissing files it — nothing is deleted.
      </Typography>

      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={40} />
              <TableCell>From</TableCell>
              <TableCell>Subject</TableCell>
              <TableCell>Order</TableCell>
              <TableCell>When</TableCell>
              <TableCell align="right">Why</TableCell>
              <TableCell align="right" width={170} />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => {
              const reason = REASON[row.reason] ?? { label: row.reason, hint: '' };
              return (
                <Fragment key={row.id}>
                  <TableRow hover>
                    <TableCell>
                      <Button size="small" sx={{ minWidth: 0, px: 0.5 }}
                        onClick={() => setOpen(open === row.id ? null : row.id)}>
                        <ExpandMoreIcon fontSize="small"
                          sx={{ transform: open === row.id ? 'rotate(180deg)' : 'none' }} />
                      </Button>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{row.from || '—'}</Typography>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 260 }}>
                      <Typography variant="body2" noWrap>{row.subject || '(no subject)'}</Typography>
                    </TableCell>
                    <TableCell>
                      {row.orderId ? (
                        <Link component={RouterLink} to={`/orders/${row.orderId}`} underline="hover">
                          <Typography variant="body2">{row.orderId.slice(0, 8)}…</Typography>
                        </Link>
                      ) : (
                        <Typography variant="body2" color="text.secondary">—</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Tooltip title={new Date(row.receivedAt as number).toLocaleString('en-IN')}>
                        <Typography variant="caption" color="text.secondary">
                          {RELATIVE(row.receivedAt as number)}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={reason.hint}>
                        <Chip size="small" variant="outlined" label={reason.label} />
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        {/* Only offered where there is a thread to admit it to.
                            Without an orderId the callable refuses, and a button
                            that always errors is worse than no button. */}
                        {row.orderId && (
                          <Button size="small" variant="contained" disabled={busy === row.id}
                            onClick={() => void decide(row.id, 'admit')}>
                            Admit
                          </Button>
                        )}
                        <Button size="small" color="inherit" disabled={busy === row.id}
                          onClick={() => void decide(row.id, 'dismiss')}>
                          Dismiss
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={7} sx={{ py: 0, border: 0 }}>
                      <Collapse in={open === row.id} unmountOnExit>
                        <Box py={2} px={1}>
                          {reason.hint && (
                            <Alert severity="info" sx={{ mb: 2 }}>{reason.hint}</Alert>
                          )}
                          <Typography variant="caption" color="text.secondary">
                            to {row.to || '—'} · {row.id}
                          </Typography>
                          {/* What will be admitted, shown exactly as it will be
                              written — then the raw mail underneath, because a
                              bad quote-strip is only visible by comparing them. */}
                          <Typography variant="body2" component="pre"
                            sx={{
                              mt: 1, whiteSpace: 'pre-wrap', fontFamily: 'inherit',
                              bgcolor: 'action.hover', p: 1.5, borderRadius: 1,
                            }}>
                            {(row.strippedBody || row.body || '(nothing)').slice(0, 4000)}
                          </Typography>
                          {row.strippedBody && row.body && row.strippedBody !== row.body && (
                            <>
                              <Typography variant="caption" color="text.secondary"
                                sx={{ mt: 2, display: 'block' }}>
                                The whole email, quoted history and all
                              </Typography>
                              <Typography variant="body2" component="pre"
                                sx={{
                                  mt: 0.5, whiteSpace: 'pre-wrap', fontFamily: 'inherit',
                                  bgcolor: 'action.hover', p: 1.5, borderRadius: 1,
                                  maxHeight: 240, overflow: 'auto', opacity: 0.75,
                                }}>
                                {row.body}
                              </Typography>
                            </>
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
}
